/**
 * WOD3D personalization assist
 * - AI (or fallback) suggestion chips next to WCPA text fields
 */
(function () {
  const ROOT_SELECTOR = '.wcpa_wrap';
  const TEXT_SELECTOR = 'input[type="text"], textarea';
  const ENHANCED_ATTR = 'data-wod3d-assist';
  /** Soft default solo para ideas AI si el módulo no declara límite. Nunca inventar en el hint. */
  const SUGGEST_FALLBACK_MAX = 24;
  const LIMIT_MIN = 2;
  const LIMIT_MAX = 200;
  const NAME_HINTS = /nombre|name|texto|grabado|inicial|personaliz/i;
  const SKIP_HINTS = /nota|notas|notes|comentario|observaci|mensaje|message|instrucci/i;
  const DEFAULT_SUGGESTIONS = ['ANA', 'BOX', 'WOD', 'TEAM', 'RX', 'GYM'];
  const PHRASE_SUGGESTIONS = [
    'AMO BURPEES, DIJO NADIE NUNCA.',
    'HOY ES DIA DE DESCANSO... JA!',
    'SOLO CINCO RONDAS MAS, VALE?',
    'DESCANSAR? QUE ES ESO?',
    'WOD: VAMOS A MORIR TODOS.',
    'CROSSFIT: PAGA POR SUFRIR.',
    'DESCANSO ES PARA LOS DEBILES.',
    'OTRA RONDA DE MUERTE LENTA.',
    'AMO LAS AGUJETAS, EN SERIO.',
    'CROSSFIT: LA ALEGRIA DEL SUFRIMIENTO.',
  ];

  const cfg = window.wod3dAiAssist || {};

  function i18n() {
    return window.wod3dI18n || {};
  }
  function t(key, fallback, vars) {
    let out = i18n()[key] || fallback || '';
    if (vars) {
      Object.keys(vars).forEach((k) => {
        out = String(out).split('{{ ' + k + ' }}').join(String(vars[k] ?? ''));
        out = String(out).split('{{' + k + '}}').join(String(vars[k] ?? ''));
      });
    }
    return out;
  }
  function currentLocale() {
    return cfg.locale || window.wod3dLocale || 'es';
  }
  function upperLocale() {
    const loc = String(currentLocale()).toLowerCase();
    if (loc.startsWith('en')) return 'en-US';
    if (loc.startsWith('pt')) return 'pt-PT';
    return 'es-ES';
  }

  function toUpper(value) {
    return String(value || '').toLocaleUpperCase(upperLocale());
  }

  function isPlausibleLimit(n) {
    return Number.isFinite(n) && n >= LIMIT_MIN && n <= LIMIT_MAX;
  }

  function parseLimitFromText(text) {
    if (!text) return null;
    const patterns = [
      /m[aá]x(?:imo|\.)?\s*:?\s*(\d+)\s*(?:caracteres?|chars?|letras?)?/i,
      /(?:hasta|l[ií]mite|limit)\s*[:\-]?\s*(\d+)\s*(?:caracteres?|chars?|letras?)?/i,
      /(\d+)\s*(?:caracteres?|chars?|letras?)\s*(?:m[aá]x(?:imo)?\.?)?/i,
      /\b(\d+)\s*\/\s*(\d+)\b/, // e.g. 0/50 counter → second number
    ];

    for (const re of patterns) {
      const match = String(text).match(re);
      if (!match) continue;
      const max = re.source.includes('\\/') ? Number(match[2]) : Number(match[1]);
      if (isPlausibleLimit(max)) return max;
    }
    return null;
  }

  /**
   * Lee el límite real del módulo WCPA (maxlength, data-*, placeholder, UI).
   * No inventa un default: si no hay config, devuelve null.
   */
  function getFieldMaxLength(field) {
    const attr = field.getAttribute('maxlength') || field.getAttribute('maxLength');
    if (attr && isPlausibleLimit(Number(attr))) return Number(attr);

    // Browsers report a huge default when maxlength is absent (~1e6+).
    if (typeof field.maxLength === 'number' && isPlausibleLimit(field.maxLength)) {
      return field.maxLength;
    }

    for (const key of ['data-max', 'data-maxlength', 'data-max-length', 'data-char-max', 'data-limit', 'max']) {
      const val = field.getAttribute(key);
      if (val && isPlausibleLimit(Number(val))) return Number(val);
    }

    // WCPA often puts the limit only in placeholder: "Máx. 50 caracteres"
    for (const text of [
      field.getAttribute('placeholder'),
      field.getAttribute('aria-label'),
      field.getAttribute('title'),
      field.getAttribute('data-placeholder'),
    ]) {
      const fromAttr = parseLimitFromText(text);
      if (fromAttr) return fromAttr;
    }

    const wrap =
      field.closest('.wcpa_field_wrap') ||
      field.closest('.form-row') ||
      field.closest('.wcpa_form_item') ||
      field.parentElement;
    if (wrap) {
      // Exclude our own assist UI so we don't re-read a stale "máx. 12" hint.
      const clone = wrap.cloneNode(true);
      clone.querySelectorAll('.wod3d-assist').forEach((el) => el.remove());
      const fromUi = parseLimitFromText(clone.textContent || '');
      if (fromUi) return fromUi;
    }

    return null;
  }

  function suggestMaxLen(fieldMax) {
    return fieldMax || SUGGEST_FALLBACK_MAX;
  }

  function hintText(fieldMax) {
    if (fieldMax) {
      return t('assistHintMax', 'Se grabará en mayúsculas · máx. {{ count }} caracteres', {
        count: fieldMax,
      });
    }
    return t('assistHint', 'Se grabará en mayúsculas');
  }

  function cleanSuggestion(value) {
    return toUpper(value)
      .replace(/[…]/g, '...')
      .replace(/[^A-Z0-9\s\-_.¿?¡!,:]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Keep only suggestions that fully fit — never mid-truncate words. */
  function fitSuggestion(value, maxLen) {
    const cleaned = cleanSuggestion(value);
    if (!cleaned || cleaned.length < 2) return '';
    if (cleaned.length <= maxLen) return cleaned;

    const noSpace = cleaned.replace(/\s+/g, '');
    if (noSpace.length >= 2 && noSpace.length <= maxLen) return noSpace;

    return '';
  }

  function clipSuggestion(value, maxLen) {
    // Used for applying to input value (hard clip). Suggestions use fitSuggestion.
    return cleanSuggestion(value).slice(0, maxLen);
  }

  function getProductTitle() {
    return (
      cfg.productTitle ||
      document.querySelector('.product__title h1')?.textContent ||
      document.querySelector('.product__title')?.textContent ||
      ''
    ).trim();
  }

  function localSuggestions(maxLen) {
    const title = getProductTitle().toLocaleLowerCase('es-ES');
    const pool = maxLen >= 12 ? [...PHRASE_SUGGESTIONS, ...DEFAULT_SUGGESTIONS] : [...DEFAULT_SUGGESTIONS];
    if (title.includes('hyrox')) pool.unshift('HYROX: PAGA POR SUFRIR.', 'OTRA ESTACION? CLARO.', 'SIN DESCANSO');
    else if (title.includes('kettle') || title.includes('kettel')) pool.unshift('MAS PESO, MAS DIVERSION?', 'DOLOR ES MI AMIGO.');
    else if (title.includes('disco')) pool.unshift('MAS PESO, MAS DIVERSION?', 'DOLOR ES MI AMIGO.');
    else if (title.includes('parche')) pool.unshift('CROSSFIT: PAGA POR SUFRIR.', 'AMO LAS AGUJETAS, EN SERIO.');
    else if (title.includes('llavero')) pool.unshift('DESCANSAR? QUE ES ESO?', 'WOD: VAMOS A MORIR TODOS.');

    return [...new Set(pool)]
      .map((s) => fitSuggestion(s, maxLen))
      .filter(Boolean)
      .slice(0, 8);
  }

  function fieldLabelText(field) {
    const wrap =
      field.closest('.wcpa_field_wrap') ||
      field.closest('.wcpa_field') ||
      field.closest('label') ||
      field.parentElement;
    if (!wrap) return '';
    const label =
      wrap.querySelector('.wcpa_field_label, label, .wcpa-label, legend') ||
      document.querySelector(`label[for="${field.id}"]`);
    return (label?.textContent || field.getAttribute('placeholder') || field.name || '').trim();
  }

  function isSkippedField(field) {
    const label = fieldLabelText(field);
    const name = field.name || '';
    const placeholder = field.getAttribute('placeholder') || '';
    return SKIP_HINTS.test(label) || SKIP_HINTS.test(name) || SKIP_HINTS.test(placeholder);
  }

  function isAssistableField(field) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return false;
    if (field instanceof HTMLInputElement && field.type !== 'text') return false;
    if (!field.closest(ROOT_SELECTOR)) return false;
    if (field.dataset.wod3dAssistBound === '1') return false;
    if (isSkippedField(field)) return false;
    const label = fieldLabelText(field);
    return NAME_HINTS.test(label) || NAME_HINTS.test(field.name || '');
  }

  function renderChips(assist, suggestions, meta = {}) {
    const chipsEl = assist.querySelector('.wod3d-assist__chips');
    const statusEl = assist.querySelector('.wod3d-assist__status');
    const maxLen = suggestMaxLen(meta.maxLen);
    if (!chipsEl) return;

    if (meta.loading) {
      chipsEl.innerHTML = '';
    } else {
      chipsEl.innerHTML = suggestions
        .map((s) => {
          const value = fitSuggestion(s, maxLen);
          if (!value) return '';
          return `<button type="button" class="wod3d-assist__chip" data-value="${value}">${value}</button>`;
        })
        .join('');
    }

    if (statusEl) {
      statusEl.textContent = meta.loading ? t('assistGenerating', 'Generando ideas…') : '';
    }
  }

  async function fetchAiSuggestions(field, maxLen) {
    if (!cfg.enabled || !cfg.endpoint) {
      return { suggestions: localSuggestions(maxLen), fromAi: false };
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 9000);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (cfg.token) headers['X-WOD3D-Token'] = cfg.token;

      const response = await fetch(cfg.endpoint, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          productTitle: getProductTitle(),
          productType: cfg.productType || '',
          productTags: cfg.productTags || [],
          fieldLabel: fieldLabelText(field),
          seed: field.value || '',
          maxLength: maxLen,
          locale: currentLocale(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      const list = Array.isArray(data.suggestions) ? data.suggestions : [];
      const cleaned = list.map((s) => fitSuggestion(s, maxLen)).filter(Boolean);
      if (!cleaned.length) return { suggestions: localSuggestions(maxLen), fromAi: false, error: true };
      return { suggestions: cleaned.slice(0, 8), fromAi: !data.fallback, error: Boolean(data.fallback) };
    } catch (_) {
      return { suggestions: localSuggestions(maxLen), fromAi: false, error: true };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function bindField(field) {
    field.dataset.wod3dAssistBound = '1';
    let fieldMax = getFieldMaxLength(field);
    let maxLen = suggestMaxLen(fieldMax);

    const host =
      field.closest('.wcpa_field_wrap') ||
      field.closest('.wcpa_field') ||
      field.parentElement;
    if (!host || host.querySelector('.wod3d-assist')) return;

    const assist = document.createElement('div');
    assist.className = 'wod3d-assist';
    assist.setAttribute(ENHANCED_ATTR, 'true');
    assist.innerHTML =
      `<div class="wod3d-assist__head">` +
      `<div class="wod3d-assist__label">${t('assistLabel', 'Ideas para grabar')}</div>` +
      `<button type="button" class="wod3d-assist__refresh">${t('assistGenerate', 'Generar ideas')}</button>` +
      `</div>` +
      `<div class="wod3d-assist__status" aria-live="polite"></div>` +
      `<div class="wod3d-assist__chips"></div>` +
      `<div class="wod3d-assist__hint">${hintText(fieldMax)}</div>`;

    const insertAfter = field.closest('.wcpa_field') || field;
    if (insertAfter.parentElement) {
      insertAfter.parentElement.insertBefore(assist, insertAfter.nextSibling);
    } else {
      host.appendChild(assist);
    }

    // Idle state: label + button only, no pills until user asks.
    renderChips(assist, [], { loading: false, maxLen });

    const refreshBtn = assist.querySelector('.wod3d-assist__refresh');

    const applyValue = (value) => {
      const next = fieldMax ? clipSuggestion(value, fieldMax) : cleanSuggestion(value);
      field.value = next;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
      field.focus();
    };

    assist.addEventListener('click', (event) => {
      const chip = event.target.closest('.wod3d-assist__chip');
      if (chip) {
        event.preventDefault();
        applyValue(chip.dataset.value || '');
        return;
      }

      const refresh = event.target.closest('.wod3d-assist__refresh');
      if (refresh) {
        event.preventDefault();
        loadAi();
      }
    });

    const loadAi = async () => {
      if (assist.classList.contains('is-loading')) return;

      // Re-read limit at click time (WCPA may inject maxlength late).
      fieldMax = getFieldMaxLength(field);
      maxLen = suggestMaxLen(fieldMax);
      const hint = assist.querySelector('.wod3d-assist__hint');
      if (hint) hint.textContent = hintText(fieldMax);

      assist.classList.add('is-loading');
      if (refreshBtn) refreshBtn.disabled = true;
      renderChips(assist, [], { loading: true, maxLen });

      let result;
      if (cfg.enabled && cfg.endpoint) {
        result = await fetchAiSuggestions(field, maxLen);
      } else {
        result = { suggestions: localSuggestions(maxLen), fromAi: false };
      }

      // Hard filter again client-side — never show oversized chips.
      result.suggestions = (result.suggestions || [])
        .map((s) => fitSuggestion(s, maxLen))
        .filter(Boolean)
        .slice(0, 8);

      assist.classList.remove('is-loading');
      if (refreshBtn) {
        refreshBtn.disabled = false;
        refreshBtn.textContent = t('assistOtherIdeas', 'Otras ideas');
      }
      renderChips(assist, result.suggestions, {
        loading: false,
        fromAi: result.fromAi,
        error: result.error,
        maxLen,
      });
    };

    const sync = () => {
      // Only hard-clip when the module declares a real limit.
      const upper = fieldMax ? clipSuggestion(field.value, fieldMax) : cleanSuggestion(field.value);
      if (field.value !== upper) {
        const start = field.selectionStart;
        const end = field.selectionEnd;
        field.value = upper;
        if (document.activeElement === field && start != null && end != null) {
          try {
            field.setSelectionRange(Math.min(start, upper.length), Math.min(end, upper.length));
          } catch (_) {}
        }
      }
    };

    field.addEventListener('input', sync);
    field.addEventListener('change', sync);
    field.addEventListener('blur', sync);
    sync();
  }

  function enhanceAll() {
    const wraps = document.querySelectorAll(ROOT_SELECTOR);
    if (!wraps.length) return;

    wraps.forEach((wrap) => {
      const fields = Array.from(wrap.querySelectorAll(TEXT_SELECTOR)).filter((field) => !isSkippedField(field));
      const named = fields.filter(isAssistableField);
      const targets = named.length ? named : fields.slice(0, 1);
      targets.forEach((field) => {
        if (field.dataset.wod3dAssistBound === '1') return;
        if (isSkippedField(field)) return;
        bindField(field);
      });
    });
  }

  function boot() {
    enhanceAll();
    const observer = new MutationObserver(() => {
      window.clearTimeout(boot._t);
      boot._t = window.setTimeout(enhanceAll, 120);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
    document.addEventListener('wod3d:addons-reset', () => {
      window.setTimeout(enhanceAll, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
